import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const MyCustomQuizzes = ({ refreshTrigger, onCountChange }) => {
  const [customQuizzes, setCustomQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  const fetchCustomQuizzes = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/quizzes/my-custom');
      if (res.data.success) {
        const quizzes = res.data.quizzes.data || [];
        setCustomQuizzes(quizzes);

        // Notify parent of quiz count
        if (onCountChange) {
          onCountChange(quizzes.length);
        }
      }
    } catch (err) {
      console.error('Error fetching custom quizzes:', err);
      setError('Không thể tải danh sách bài tập tùy chỉnh');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    fetchCustomQuizzes();
  }, [refreshTrigger, fetchCustomQuizzes]);

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa bài tập này? Hành động này không thể hoàn tác.')) {
      return;
    }

    setDeletingId(quizId);
    try {
      await api.delete(`/quizzes/custom/${quizId}`);
      const updatedQuizzes = customQuizzes.filter(quiz => quiz._id !== quizId);
      setCustomQuizzes(updatedQuizzes);

      // Notify parent of updated count
      if (onCountChange) {
        onCountChange(updatedQuizzes.length);
      }
    } catch (err) {
      console.error('Error deleting quiz:', err);
      alert(
        err.response?.data?.errors?.[0]?.msg ||
        err.response?.data?.msg ||
        'Không thể xóa bài tập'
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleTakeQuiz = (quizId) => {
    navigate(`/quiz/${quizId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  if (customQuizzes.length === 0) {
    return (
      <div className="text-center p-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
        <svg
          className="mx-auto h-16 w-16 text-gray-400 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Chưa có bài tập tùy chỉnh</h3>
        <p className="text-gray-600 mb-4">
          Tạo bài tập đầu tiên của bạn để bắt đầu luyện tập
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Bài Tập Tùy Chỉnh Của Tôi</h2>

      <div className="grid gap-4 md:grid-cols-2">
        {customQuizzes.map(quiz => (
          <div
            key={quiz._id}
            className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {quiz.title}
                </h3>

                {quiz.description && (
                  <p className="text-gray-600 text-sm mb-3">{quiz.description}</p>
                )}

                <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-3">
                  <span className="flex items-center bg-blue-50 px-3 py-1 rounded-full">
                    <svg className="w-4 h-4 mr-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="font-medium text-blue-700">{quiz.questions?.length || 0} câu hỏi</span>
                  </span>

                  {quiz.timeLimit && (
                    <span className="flex items-center bg-purple-50 px-3 py-1 rounded-full">
                      <svg className="w-4 h-4 mr-1 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium text-purple-700">{quiz.timeLimit} phút</span>
                    </span>
                  )}

                  <span className="flex items-center bg-gray-50 px-3 py-1 rounded-full">
                    <svg className="w-4 h-4 mr-1 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-gray-600">{new Date(quiz.createdAt).toLocaleDateString('vi-VN')}</span>
                  </span>
                </div>

                {/* Source Quizzes Info */}
                {quiz.sourceQuizzes && quiz.sourceQuizzes.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-gray-500 mb-2">Nguồn từ các bài tập:</p>
                    <div className="flex flex-wrap gap-2">
                      {quiz.sourceQuizzes.map(sourceQuiz => (
                        <span
                          key={sourceQuiz._id}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-800 border border-indigo-200"
                        >
                          {sourceQuiz.title}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Attempt Info */}
                {quiz.lastAttempt && (
                  <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-green-700 mb-1">Điểm cao nhất:</p>
                        <p className="text-lg font-bold text-green-800">
                          {quiz.lastAttempt.score} / {quiz.lastAttempt.totalPoints}
                          <span className="text-sm ml-2">({quiz.lastAttempt.percentage.toFixed(1)}%)</span>
                        </p>
                      </div>
                      <div className="text-4xl">
                        {quiz.lastAttempt.percentage >= 80 ? '🎉' :
                         quiz.lastAttempt.percentage >= 60 ? '😊' : '📚'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t border-gray-200">
              <button
                onClick={() => handleTakeQuiz(quiz._id)}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all shadow-sm"
              >
                {quiz.lastAttempt ? '🔄 Làm lại' : '▶ Bắt đầu'}
              </button>

              <button
                onClick={() => handleDeleteQuiz(quiz._id)}
                disabled={deletingId === quiz._id}
                className="px-4 py-2.5 bg-red-50 text-red-700 border border-red-200 rounded-lg font-semibold hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {deletingId === quiz._id ? '⏳' : '🗑️'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyCustomQuizzes;
